import {
  Directive,
  ElementRef,
  Renderer2,
  NgZone,
  Input,
  OnInit,
  OnDestroy,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[appColumnResize]',
  standalone: true,
})
export class ColumnResizeDirective implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() resizableTable: HTMLElement | null = null;

  private startX!: number;
  private startWidth!: number;
  private isResizing = false;
  private column: HTMLElement;
  private resizer: HTMLElement | null = null;
  private destroy$ = new Subject<void>();
  private canResize = false;
  private resizeObserver: ResizeObserver | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private zone: NgZone,
  ) {
    this.column = this.el.nativeElement;
  }

  ngOnInit() {
    this.createResizer();
    this.initializeResizeListener();
  }

  ngAfterViewInit() {
    this.rebindOverflowObserver();
    this.updateResizeAvailability();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.resizableTable) {
      return;
    }

    this.rebindOverflowObserver();
    this.updateResizeAvailability();
  }

  private createResizer() {
    const resizer = this.renderer.createElement('div');

    [
      'absolute',
      'top-0',
      'right-0',
      'bottom-0',
      'width-[10px]',
      'cursor-col-resize',
      'z-index-1',
      'bg-gray-300',
      'opacity-30',
      'hover:opacity-100',
      'rounded-full',
      'my-2',
      'transition-color',
      'hidden',
      'data-[resizable=true]:block',
    ].forEach(clazz => this.renderer.addClass(resizer, clazz));
    this.renderer.setStyle(resizer, 'position', 'absolute');
    this.renderer.setStyle(resizer, 'right', '0');
    this.renderer.setStyle(resizer, 'top', '0');
    this.renderer.setStyle(resizer, 'width', '3px');
    this.renderer.setStyle(resizer, 'cursor', 'col-resize');
    this.renderer.setAttribute(resizer, 'data-resizable', 'false');
    this.renderer.appendChild(this.column, resizer);

    this.resizer = resizer;
  }

  private initializeResizeListener() {
    this.zone.runOutsideAngular(() => {
      if (!this.resizer) {
        return;
      }

      fromEvent(this.resizer, 'mousedown')
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: MouseEvent) => this.onMouseDown(event));

      fromEvent(document, 'mousemove')
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: MouseEvent) => this.onMouseMove(event));

      fromEvent(document, 'mouseup')
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: MouseEvent) => this.onMouseUp(event));
    });
  }

  private onMouseDown(event: MouseEvent) {
    if (!this.canResize) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.isResizing = true;
    this.startX = event.pageX;
    this.startWidth = this.column.offsetWidth;
  }

  private onMouseMove(event: MouseEvent) {
    if (!this.isResizing || !this.canResize) {
      return;
    }
    const width = this.startWidth + (event.pageX - this.startX);
    this.renderer.setStyle(this.column, 'min-width', `${width}px`);
    this.renderer.setStyle(this.column, 'max-width', `${width}px`);
  }

  private onMouseUp(event: MouseEvent) {
    event.stopPropagation();
    if (!this.isResizing) {
      return;
    }
    this.isResizing = false;
  }

  private initializeOverflowObserver() {
    if (!this.resizableTable || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => this.updateResizeAvailability());
    this.resizeObserver.observe(this.resizableTable);
  }

  private rebindOverflowObserver() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.initializeOverflowObserver();
  }

  private updateResizeAvailability() {
    const table = this.resizableTable;

    this.canResize = !!table && table.scrollWidth > table.clientWidth;
    if (!this.resizer) {
      return;
    }
    this.renderer.setAttribute(this.resizer, 'data-resizable', this.canResize ? 'true' : 'false');

    if (!this.canResize) {
      this.isResizing = false;
      this.renderer.removeStyle(this.column, 'min-width');
      this.renderer.removeStyle(this.column, 'max-width');
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.resizer = null;
  }
}
